import React, { useCallback,useMemo } from 'react';
import { Anchor,Box, Inscription } from '@common-fe/common-fe';

import ROUTES from '@/common/routes';
import { BlockWrapper } from '@/components/wrappers';
import LogChangesList from '@/modules/ActivityLog/LogList/LogChangesList/LogChangesList';
import { formatAttribute } from '@/modules/ActivityLog/LogList/LogItem/Changeset/common';
import useGetActivityLogChangesetsQuery from '@/modules/ActivityLog/LogList/LogItem/Changeset/useGetActivityLogChangesets.query';
import useGetActivityLogsQuery, { Actions, ActivityLog } from '@/modules/ActivityLog/LogList/useGetActivityLogs.query';
import { LockboxType } from '@/modules/employer/components/Lockbox/lockbox.types';
import LockboxInfoContainer from '@/modules/employer/components/Lockbox/LockboxDetails/components/LockboxInfo/LockboxInfoContainer';
import useLockboxDetailsStore from '@/modules/employer/components/Lockbox/LockboxDetails/stores/useLockboxDetails.store';

const lockboxLogFields = new Set([
  'lockboxType',
  'lockboxStatus',
  'reason',
  'employeeName',
  'liquidateAllFunds',
  'notes',
  'checkDetails/vendorInfo.vendorName',
  'checkDetails.benefitOf',
  'checkDetails/vendorInfo.addressLine1',
  'checkDetails/vendorInfo.addressLine2',
  'checkDetails/vendorInfo.city',
  'checkDetails/vendorInfo.state',
  'checkDetails/vendorInfo.zip',
  'amount',
  'checkDetails/vendorInfo.checkName',
  'employeeId',
  'paymentMethodType',
  'wireStatusType',
  'parentLockbox',
  'destinationType',
  'remainderAmount',
  'payTo',
  'accountId',
  'benefitOf',
  'addressLine1',
  'addressLine2',
  'city',
  'state',
  'zip',
  'status',
]);

const modifiedLogFields = {
  status: 'lockboxCheckStatus'
};

const RETURNED_MAIL_APPROVED_LABEL = 'Approved';
const RETURNED_MAIL_PROCESSED_LABEL = 'Processed (marked as bad address)';

const dynamicFieldPatterns = [
  /^claimRepayments\/\d+\/id\.claimId$/,
  /^claimRepayments\/\d+\.repaymentAmount$/,
];

const sortLogs = (logs?: ActivityLog[]) => logs?.sort((prev, next) => new Date(next?.changedAt).getTime() - new Date(prev?.changedAt).getTime());

const LockboxLogChanges = () => {
  const { originalLockbox, isDividingMode } = useLockboxDetailsStore();

  const { logs, logsIds, total, isFetching } = useGetActivityLogsQuery({
    lockboxId: originalLockbox?.id,
    page: 1,
    size: 100,
    actions: [Actions.update, Actions.create],
    disabled: !originalLockbox?.id,
  });

  const {
    formattedData,
    isLoading: areChangesetsLoading,
  } = useGetActivityLogChangesetsQuery(logsIds);

  const sortedLogs = useMemo(() => sortLogs(logs), [logs]);

  const handleRedirectToParentDocument = useCallback((documentId?: string) => {
    if (documentId) {
      window.open(ROUTES.LOCKBOX_DETAILS(documentId), '_blank');
    }
  }, []);

  const preparedChangesets = useMemo(() => formattedData
    ?.filter((changeLog) => {
      const fieldName = changeLog?.fieldName;
      if (!fieldName) return false;
      if (lockboxLogFields.has(fieldName)) return true;
      return dynamicFieldPatterns.some(pattern => pattern.test(fieldName));
    })
    .map((changeLog) => {
      const fieldName = changeLog?.fieldName as keyof typeof modifiedLogFields;
      if (fieldName && fieldName in modifiedLogFields) {
        return { ...changeLog, attribute: formatAttribute(modifiedLogFields[fieldName]) };
      }
      return changeLog;
    })
    .map((changeLog) => changeLog?.fieldName === 'parentLockbox' ? ({
      ...changeLog,
      updatedNode: (
        <Anchor
          onClick={() => handleRedirectToParentDocument(changeLog?.updated)}
          size="medium"
          weight={700}
        >
          ID {changeLog?.updated}
        </Anchor>
      )
    }) : changeLog)
    .map((changeLog) => {
      if (
        changeLog?.fieldName === 'lockboxStatus' &&
        originalLockbox?.lockboxType === LockboxType.RETURNED_MAIL
      ) {
        return {
          ...changeLog,
          previous: changeLog?.previous === RETURNED_MAIL_APPROVED_LABEL
            ? RETURNED_MAIL_PROCESSED_LABEL
            : changeLog?.previous,
          updated: changeLog?.updated === RETURNED_MAIL_APPROVED_LABEL
            ? RETURNED_MAIL_PROCESSED_LABEL
            : changeLog?.updated,
        };
      }
      return changeLog;
    }),
  [formattedData, handleRedirectToParentDocument, originalLockbox]);

  if (isFetching || !total || areChangesetsLoading || isDividingMode) return null;

  return (
    <LockboxInfoContainer
      margin={{ top: 'spacing8'}}
      pad={{ horizontal: 'spacing24', vertical: 'spacing12' }}
      data-testid="lockbox-log-changes_wrapper"
    >
      <BlockWrapper
        title={(
          <Box>
            <Inscription
              weight="normal"
              color="textSecondary"
              size="14px"
              lineHeight="20px"
            >
              {`Log changes (${total})`}
            </Inscription>
          </Box>
        )}
        iconColor="textSecondary"
        defaultExpandedValue={false}
        isPureChild
      >
        <Box
          data-testid="lockbox-log-changes_list"
          margin={{ top: 'spacing8' }}
        >
          <LogChangesList logs={sortedLogs} changesets={preparedChangesets} />
        </Box>
      </BlockWrapper>
    </LockboxInfoContainer>
  );
};

export default LockboxLogChanges;
